<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TeletelFlowsAPI from 'dashboard/api/teletel/flows';
import { countNodes } from './components/treeOps';

const store = useStore();
const { t } = useI18n();
const router = useRouter();

const templates = computed(() => store.getters['teletelFlows/getTemplates']);
const uiFlags = computed(() => store.getters['teletelFlows/getUIFlags']);
const activeCategory = ref('all');
const instantiatingId = ref(null);
const templateNodeCounts = ref({});

const CATEGORY_KEYS = [
  'all',
  'auto_responder',
  'business',
  'routing',
  'supervision',
];

const categories = computed(() => {
  const present = new Set(templates.value.map(tpl => tpl.category));
  return CATEGORY_KEYS.filter(key => key === 'all' || present.has(key));
});

const visibleTemplates = computed(() =>
  activeCategory.value === 'all'
    ? templates.value
    : templates.value.filter(tpl => tpl.category === activeCategory.value)
);

onMounted(async () => {
  await store.dispatch('teletelFlows/fetchTemplates');
  const details = await Promise.all(
    templates.value.map(async template => {
      try {
        const { data } = await TeletelFlowsAPI.getTemplate(template.id);
        return [template.id, countNodes(data.payload?.tree)];
      } catch {
        return [template.id, null];
      }
    })
  );
  templateNodeCounts.value = Object.fromEntries(details);
});

const useTemplate = async template => {
  if (instantiatingId.value) return;
  instantiatingId.value = template.id;
  try {
    const flow = await store.dispatch('teletelFlows/instantiateTemplate', {
      templateId: template.id,
      name: template.name,
    });
    router.push({ name: 'teletel_flows_edit', params: { flowId: flow.id } });
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('TELETEL_FLOWS.TEMPLATES.ERROR')
    );
  } finally {
    instantiatingId.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6 overflow-auto">
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ t('TELETEL_FLOWS.TEMPLATES.HEADER') }}
        </h1>
        <p class="text-sm text-n-slate-11 max-w-2xl">
          {{ t('TELETEL_FLOWS.TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <NextButton slate sm @click="router.push({ name: 'teletel_flows_list' })">
        {{ t('TELETEL_FLOWS.TEMPLATES.BACK') }}
      </NextButton>
    </div>

    <div class="flex flex-wrap gap-2">
      <NextButton
        v-for="cat in categories"
        :key="cat"
        :slate="activeCategory !== cat"
        :teal="activeCategory === cat"
        xs
        @click="activeCategory = cat"
      >
        {{ t(`TELETEL_FLOWS.TEMPLATES.CATEGORIES.${cat}`) }}
      </NextButton>
    </div>

    <p
      v-if="!uiFlags.isFetching && !visibleTemplates.length"
      class="text-n-slate-11"
    >
      {{ t('TELETEL_FLOWS.TEMPLATES.EMPTY') }}
    </p>

    <div class="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
      <div
        v-for="template in visibleTemplates"
        :key="template.id"
        class="flex flex-col gap-2 p-4 border rounded-lg border-n-weak bg-n-solid-1"
      >
        <div class="flex items-center gap-2">
          <span
            class="flex items-center justify-center rounded-lg size-8 bg-n-alpha-2 text-n-slate-12"
          >
            <Icon :icon="template.icon || 'i-lucide-workflow'" class="size-4" />
          </span>
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ template.name }}
          </h2>
        </div>
        <p class="flex-1 text-xs text-n-slate-11">
          {{ template.description }}
        </p>
        <dl class="grid grid-cols-2 gap-2 text-xs text-n-slate-10">
          <div>
            <dt>{{ t('TELETEL_FLOWS.TEMPLATES.TRIGGER') }}</dt>
            <dd class="mt-0.5 text-n-slate-12">
              {{ t(`TELETEL_FLOWS.TRIGGERS.${template.trigger_type}`) }}
            </dd>
          </div>
          <div
            v-if="
              templateNodeCounts[template.id] !== null &&
              templateNodeCounts[template.id] !== undefined
            "
          >
            <dt>{{ t('TELETEL_FLOWS.TEMPLATES.NODES') }}</dt>
            <dd class="mt-0.5 text-n-slate-12">
              {{
                t('TELETEL_FLOWS.TEMPLATES.NODE_COUNT', {
                  count: templateNodeCounts[template.id],
                })
              }}
            </dd>
          </div>
        </dl>
        <div class="flex items-center justify-between">
          <span class="text-xs text-n-slate-10">
            {{ t(`TELETEL_FLOWS.TEMPLATES.CATEGORIES.${template.category}`) }}
          </span>
          <NextButton
            sm
            :is-loading="instantiatingId === template.id"
            :disabled="Boolean(instantiatingId)"
            @click="useTemplate(template)"
          >
            {{ t('TELETEL_FLOWS.TEMPLATES.USE') }}
          </NextButton>
        </div>
      </div>
    </div>
  </div>
</template>
